/**
 * WeatherLayerProvider
 *
 * Implementation of the LayerProvider interface for weather data.
 */

import mapboxgl from 'mapbox-gl';
import { renderWindBarbCanvas } from './WindBarbPng';
import { BaseLayerProvider } from '../BaseLayerProvider';
import { clampOpacity } from '../markerOpacity';
import '../WeatherStationPopup.scss';
import '../WeatherStationHoverWidget.scss';
import {
  LayerDataConfig,
  LayerProcessingConfig,
  LayerRenderingConfig,
  LayerOptions,
  LayerHelpers
} from '../interfaces';
import { logger } from '../../../utils/logger';
import { getBackendUrl, getWeatherConditionNested } from '../../../services/configService';

const log = logger('WeatherLayerProvider');

// Mapbox source/layer ids for the GPU-rendered weather symbol layer. The whole
// live-weather layer is now ONE geojson source + ONE symbol layer (#performance):
// each station's full wind barb is baked into an `addImage` icon and placed via
// `icon-image`. This replaces the two-DOM-markers-per-station approach, whose
// per-render-frame repositioning cost dominated profiles (Mapbox repositions
// every HTML marker on every frame; symbol layers do not).
const WX_SOURCE_ID = 'live-weather-source';
const WX_LAYER_ID = 'live-weather-layer';

// Bookkeeping for idempotent enable/refresh/teardown. We track the icon ids we
// added so cleanup can `removeImage` exactly those, and we keep handles to the
// shared hover/click popups and the bound map handlers so they can be removed.
interface WeatherLayerState {
  iconIds: string[];
  hoverPopup: mapboxgl.Popup | null;
  handlers: {
    mouseenter: (e: mapboxgl.MapLayerMouseEvent) => void;
    mousemove: (e: mapboxgl.MapLayerMouseEvent) => void;
    mouseleave: () => void;
    click: (e: mapboxgl.MapLayerMouseEvent) => void;
  } | null;
  map: mapboxgl.Map | null;
}

// Add type definition for window to include weather markers/popups. Kept so
// other code that reads `window.weatherMarkers` (markerOpacity, vite-env.d.ts)
// doesn't break; the symbol-layer path leaves it an empty array.
declare global {
  interface Window {
    weatherMarkers?: mapboxgl.Marker[];
    weatherPopups?: mapboxgl.Popup[];
  }
}

/**
 * Helper functions for weather popup color logic
 */

/**
 * Determines the background color for the temperature bar
 * @param temp - Temperature in °C
 * @param rh - Relative humidity %
 * @returns Hex color code
 */
export function getTempColor(temp: number | undefined, rh: number | undefined): string {
  if (temp === undefined) return '#CCCCCC';
  if (rh !== undefined && temp >= rh) return '#FF0000';  // Crossover condition (inclusive: temp >= rh)
  if (temp > 25) return '#FFA500';  // Hot
  if (temp > 15) return '#00FFFF';  // Mild (15 < temp <= 25)
  return '#0000FF';  // Cold (temp <= 15)
}

/**
 * Determines the background color for the humidity bar
 * @param rh - Relative humidity %
 * @param temp - Temperature in °C
 * @returns Hex color code
 */
export function getRHColor(rh: number | undefined, temp: number | undefined): string {
  if (rh === undefined) return '#CCCCCC';
  if (temp !== undefined && temp >= rh) return '#FF0000';  // Crossover condition (inclusive: temp >= rh)
  if (rh < 25) return '#FFA500';  // Very dry
  if (rh <= 75) return '#00FFFF';  // Normal
  return '#0000FF';  // Humid
}

/**
 * Checks if weather data is stale (older than 180 minutes)
 * @param dateStr - ISO 8601 date string
 * @returns true if data is stale
 */
function isDataStale(dateStr: string | undefined): boolean {
  if (!dateStr) return true;
  const recordDate = new Date(dateStr);
  const now = new Date();
  const diffMinutes = (now.getTime() - recordDate.getTime()) / (1000 * 60);
  return diffMinutes > 180;
}

/**
 * Formats date to YYYY-MM-DD HH:mm:ss
 * @param dateStr - ISO 8601 date string
 * @returns Formatted date string
 */
function formatRecordDate(dateStr: string | undefined): string {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * Shape of the `properties` we attach to each GeoJSON point feature. These carry
 * everything the hover/click popups need, so the popup handlers never reach back
 * into the raw source data — they read straight off `feature.properties`.
 *
 * Mapbox stringifies/serializes feature properties through its tile pipeline, so
 * numeric fields are stored as numbers and may come back as numbers; we read them
 * with the same `undefined`-aware formatting the DOM markers used.
 */
interface WeatherFeatureProps {
  iconId: string;
  stationName: string;
  nesdis: string | number | undefined;
  temp: number | undefined;
  rh: number | undefined;
  wspd: number | undefined;
  gust: number | undefined;
  dir: number | undefined;
  rain: number | undefined;
  recordDate: string | undefined;
}

/**
 * Read a possibly-stringified numeric property back as a number (Mapbox can
 * round-trip feature props through string form). Returns undefined for
 * missing/blank/non-numeric values so the existing `undefined` formatting paths
 * (`??`, `N/A`) behave identically to the DOM-marker version.
 */
function numProp(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(n) ? undefined : n;
}

/**
 * Build the compact hover-widget popup HTML. Markup is byte-identical to the
 * per-marker `mouseenter` handler that previously lived inline.
 */
function buildHoverPopupContent(p: WeatherFeatureProps): string {
  const isStale = isDataStale(p.recordDate);

  // Calculate colors based on temp/rh
  const tempColor = getTempColor(p.temp, p.rh);
  const rhColor = getRHColor(p.rh, p.temp);

  // Wind arrow rotation: wind direction shows WHERE wind comes FROM,
  // so arrow points WHERE wind is going TO (opposite direction)
  const arrowRotation = p.dir !== undefined ? (p.dir + 180) % 360 : 0;

  // Format display values
  const tempDisplayValue = p.temp !== undefined ? `${Math.round(p.temp)}°C` : '??';
  const rhDisplayValue = p.rh !== undefined ? `${Math.round(p.rh)}%` : '??';
  const wspdDisplayValue = p.wspd !== undefined ? `${Math.round(p.wspd)}` : '??';
  const gustDisplayValue = p.gust !== undefined && p.gust <= 9999 ? `(${Math.round(p.gust)})` : '';

  return `
                <div class="hover-widget-wrapper">
                  <div class="hover-station-name">${p.stationName}</div>
                  <div class="weather-station-hover-widget">
                    <div class="weather-hover-container">
                      <!-- Left Temperature Bar -->
                      <div class="hover-temp-bar" style="background-color: ${tempColor};"></div>

                      <!-- Center Content -->
                      <div class="hover-wx-content${isStale ? ' stale' : ''}">
                        <div class="hover-temp-display">${tempDisplayValue}</div>
                        <div class="hover-rh-display">${rhDisplayValue}</div>
                        <div class="hover-wind-display">
                          <i class="fa-solid fa-arrow-up" style="transform: rotate(${arrowRotation}deg);"></i>
                          <br />
                          ${wspdDisplayValue} ${gustDisplayValue}
                        </div>
                      </div>

                      <!-- Right Humidity Bar -->
                      <div class="hover-rh-bar" style="background-color: ${rhColor};"></div>
                    </div>
                  </div>
                </div>
              `;
}

/**
 * Build the detailed click popup HTML. Markup is byte-identical to the per-marker
 * `click` handler that previously lived inline (`.weather-station-container` +
 * `.wse-popup-actions`).
 */
export function buildClickPopupContent(p: WeatherFeatureProps): string {
  const isStale = isDataStale(p.recordDate);
  const formattedDate = formatRecordDate(p.recordDate);

  // Calculate colors based on temp/rh
  const tempColor = getTempColor(p.temp, p.rh);
  const rhColor = getRHColor(p.rh, p.temp);

  // Wind arrow rotation: wind direction shows WHERE wind comes FROM,
  // so arrow points WHERE wind is going TO (opposite direction)
  const arrowRotation = p.dir !== undefined ? (p.dir + 180) % 360 : 0;

  // Format display values
  const tempDisplayValue = p.temp !== undefined ? `${Math.round(p.temp)}°C` : 'N/A';
  const rhDisplayValue = p.rh !== undefined ? `${Math.round(p.rh)}%` : 'N/A';
  const wspdDisplayValue = p.wspd !== undefined ? `${Math.round(p.wspd)} km/h` : 'N/A';
  const gustDisplayValue = p.gust !== undefined && p.gust <= 9999 ? `Gusting ${Math.round(p.gust)}` : '';
  // The WFS layer serves current_weather.precipitation — the hourly reading for
  // the station's latest observation. It previously served rain_total, a
  // lifetime accumulator, displayed as a bare "mm"; that is how values above
  // 3000mm went unquestioned (#752, #753, #754). The period is stated so the
  // figure cannot be read as a daily or seasonal total.
  const rainDisplayValue = p.rain !== undefined
    ? `<i class="fa-solid fa-cloud-rain"></i> ${p.rain.toFixed(1)} mm/1hr`
    : 'N/A';

  return `
                <div class="weather-station-container">
                  <!-- Left Temperature Bar -->
                  <div class="temp-bar" style="background-color: ${tempColor};">
                    <i class="fa-solid fa-temperature-low"></i>
                    <div class="temp-bar-label">Temperature</div>
                    <div class="legend-vertical">
                      <div class="legend-item">
                        <div class="color-box" style="background-color: #FF0000;"></div>
                        <div class="legend-text">Xover</div>
                      </div>
                      <div class="legend-item">
                        <div class="color-box" style="background-color: #FFA500;"></div>
                        <div class="legend-text">HOT</div>
                      </div>
                      <div class="legend-item">
                        <div class="color-box" style="background-color: #00FFFF;"></div>
                        <div class="legend-text">MILD</div>
                      </div>
                      <div class="legend-item">
                        <div class="color-box" style="background-color: #0000FF;"></div>
                        <div class="legend-text">COLD</div>
                      </div>
                    </div>
                  </div>

                  <!-- Center Content -->
                  <div class="wx-content-wrapper${isStale ? ' stale' : ''}">
                    <div class="station-name">${p.stationName}</div>
                    <div class="temp-display-large">${tempDisplayValue}</div>
                    <div class="rh-display-large">${rhDisplayValue}</div>
                    <div class="wind-display-large">
                      <i class="fa-solid fa-arrow-up" style="transform: rotate(${arrowRotation}deg);"></i>
                      <br />
                      ${wspdDisplayValue}
                      <br />
                      ${gustDisplayValue}
                      <div class="rain-display-large">${rainDisplayValue}</div>
                      <div class="record-date">
                        Transmitted:<br />${formattedDate}
                      </div>
                    </div>
                  </div>

                  <!-- Right Humidity Bar -->
                  <div class="rh-bar" style="background-color: ${rhColor};">
                    <i class="fa-solid fa-droplet"></i>
                    <div class="rh-bar-label">Humidity</div>
                    <div class="legend-vertical">
                      <div class="legend-item">
                        <div class="color-box" style="background-color: #FF0000;"></div>
                        <div class="legend-text">Xover</div>
                      </div>
                      <div class="legend-item">
                        <div class="color-box" style="background-color: #FFA500;"></div>
                        <div class="legend-text">DRY</div>
                      </div>
                      <div class="legend-item">
                        <div class="color-box" style="background-color: #00FFFF;"></div>
                        <div class="legend-text">NORM</div>
                      </div>
                      <div class="legend-item">
                        <div class="color-box" style="background-color: #0000FF;"></div>
                        <div class="legend-text">DAMP</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="wse-popup-actions">
                  <button type="button" class="wse-popup-details-btn">Details</button>
                </div>
              `;
}

/**
 * Sanitize a station name into a Mapbox-safe, deterministic icon id. Each station
 * has exactly one current reading, so a name-keyed id is stable for the lifetime
 * of one data refresh and lets us guard `hasImage`/`removeImage` cleanly.
 */
function iconIdForStation(stationName: string): string {
  return 'wx-' + stationName.replace(/[^A-Za-z0-9_-]/g, '_');
}

/**
 * Tear down the symbol layer, source, registered icons, shared hover popup, any
 * open click popups, and the bound map handlers. Idempotent — safe to call when
 * nothing was added (initial enable, double-disable).
 */
function teardownWeatherSymbolLayer(state: WeatherLayerState): void {
  const map = state.map;

  // Remove bound layer handlers first so nothing fires mid-teardown.
  if (map && state.handlers) {
    map.off('mouseenter', WX_LAYER_ID, state.handlers.mouseenter);
    map.off('mousemove', WX_LAYER_ID, state.handlers.mousemove);
    map.off('mouseleave', WX_LAYER_ID, state.handlers.mouseleave);
    map.off('click', WX_LAYER_ID, state.handlers.click);
  }
  state.handlers = null;

  // Shared hover popup + any open detailed click popups.
  if (state.hoverPopup) {
    state.hoverPopup.remove();
    state.hoverPopup = null;
  }
  document.querySelectorAll('.mapboxgl-popup.weather-popup, .mapboxgl-popup.weather-popup-detail')
    .forEach(popup => popup.remove());
  if (window.weatherPopups) {
    window.weatherPopups.forEach(popup => popup.remove());
    window.weatherPopups = [];
  }

  if (map) {
    if (map.getLayer(WX_LAYER_ID)) map.removeLayer(WX_LAYER_ID);
    if (map.getSource(WX_SOURCE_ID)) map.removeSource(WX_SOURCE_ID);
    for (const iconId of state.iconIds) {
      if (map.hasImage(iconId)) map.removeImage(iconId);
    }
  }
  state.iconIds = [];

  // Legacy bookkeeping: other code (markerOpacity, vite-env typings) reads
  // window.weatherMarkers; keep it a defined-but-empty array.
  window.weatherMarkers = [];
}

/**
 * Render the single GeoJSON source + symbol layer for live weather. Each fresh
 * station's FULL wind barb (rotation baked in) is registered as an `addImage`
 * icon and placed via `icon-image`. Replaces the prior two-DOM-markers-per-station
 * approach. Re-runnable: tears down the previous layer/icons first.
 */
function renderWeatherSymbolLayer(
  state: WeatherLayerState,
  map: mapboxgl.Map,
  data: GeoJSON.FeatureCollection,
): void {
  state.map = map;

  // Clear the prior render (layer/source/icons/popups/handlers) so a data
  // refresh doesn't leak stale icons or stack duplicate handlers.
  teardownWeatherSymbolLayer(state);

  if (!data || !data.features) return;

  // Only show stations reporting within the last 2 hours (current/previous hour).
  const now = new Date();
  const cutoff = new Date(now.getTime() - 2 * 60 * 60 * 1000);
  const freshFeatures = data.features.filter((feature: GeoJSON.Feature) => {
    const dateStr = feature.properties?.['header.xmitDateUtcStr'] ?? feature.properties?.observed_at;
    if (!dateStr) return false;
    const recordDate = new Date(dateStr);
    return recordDate >= cutoff;
  });
  log.debug(`Weather stations: ${data.features.length} total, ${freshFeatures.length} within last 2 hours`);

  // Visual size multiplier for the station barbs (~0.5 ≈ the old DOM-marker size).
  const ICON_SCALE = 1.0;
  // Icons are drawn at ICON_SCALE×devicePixelRatio for crisp, larger barbs, then
  // displayed via the matching `pixelRatio` so HiDPI stays sharp.
  const pixelRatio = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;

  const outFeatures: GeoJSON.Feature[] = [];
  const usedIconIds = new Set<string>();

  for (const feature of freshFeatures) {
    if (!feature.geometry || feature.geometry.type !== 'Point') continue;
    const props = feature.properties;
    if (!props) continue;

    const coords = feature.geometry.coordinates as [number, number];
    const stationName = props['header.name'] || props.name || 'Unknown Station';

    // Same number coercion as the DOM markers: only render a barb when speed and
    // direction are real numbers; gust capped at 9999 → 0.
    const wspdNumber = typeof props.wspd === 'number' ? Math.round(props.wspd) : null;
    const dirNumber = typeof props.dir === 'number' ? props.dir : null;
    const tempNumber = typeof props.temp === 'number' ? Math.round(props.temp) : null;
    const rhNumber = typeof props.rh === 'number' ? Math.round(props.rh) : null;
    const gustNumber = typeof props.gust === 'number' ? (props.gust > 9999) ? 0 : Math.round(props.gust) : null;

    if (wspdNumber === null || dirNumber === null) continue;

    // FULL barb (shaft+barbs+head), rotation baked in → no icon-rotate needed.
    // ICON_SCALE is the visual size multiplier; multiplying by pixelRatio keeps
    // it crisp on HiDPI. Tune ICON_SCALE to resize the station barbs.
    const canvas = renderWindBarbCanvas({
      speed: wspdNumber ?? 1,
      direction: dirNumber ?? 1.1,
      temp: tempNumber ?? 1,
      rh: rhNumber ?? 1,
      gust: gustNumber ?? 1,
      size: 90,
      scale: ICON_SCALE * pixelRatio,
      renderPart: 'all',
    });

    const ctx = canvas.getContext('2d');
    if (!ctx) continue;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    const iconId = iconIdForStation(stationName);
    // Guard against duplicate station names within one render.
    if (!usedIconIds.has(iconId)) {
      usedIconIds.add(iconId);
      state.iconIds.push(iconId);
      // Mapbox accepts a StyleImageInterface-like {width,height,data}. Re-add
      // each refresh so the icon reflects the latest reading.
      if (map.hasImage(iconId)) map.removeImage(iconId);
      map.addImage(
        iconId,
        { width: imageData.width, height: imageData.height, data: imageData.data },
        { pixelRatio },
      );
    }

    // Carry everything the popups need on the feature itself.
    const featureProps: WeatherFeatureProps = {
      iconId,
      stationName,
      nesdis: props.nesdis ?? props['header.nesdis'],
      temp: numProp(props.temp),
      rh: numProp(props.rh),
      wspd: numProp(props.wspd),
      gust: numProp(props.gust),
      dir: numProp(props.dir),
      rain: numProp(props.rain),
      recordDate: props['header.xmitDateUtcStr'] ?? props.observed_at,
    };

    outFeatures.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: coords },
      properties: featureProps as unknown as GeoJSON.GeoJsonProperties,
    });
  }

  const fc: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: outFeatures };

  // Idempotent add (teardown above already cleared, but guard anyway).
  if (!map.getSource(WX_SOURCE_ID)) {
    map.addSource(WX_SOURCE_ID, { type: 'geojson', data: fc });
  } else {
    (map.getSource(WX_SOURCE_ID) as mapboxgl.GeoJSONSource).setData(fc);
  }

  if (!map.getLayer(WX_LAYER_ID)) {
    map.addLayer({
      id: WX_LAYER_ID,
      type: 'symbol',
      source: WX_SOURCE_ID,
      layout: {
        'icon-image': ['get', 'iconId'],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center',
        // Size is handled by ICON_SCALE in the rendered icon (crisp), not here.
        // Rotation is baked into the icon, so do NOT set icon-rotate.
      },
    });
  }

  // Read feature props off a queried/event feature back into our typed shape.
  const propsFromFeature = (feature: mapboxgl.MapboxGeoJSONFeature): WeatherFeatureProps => {
    const fp = feature.properties || {};
    return {
      iconId: fp.iconId,
      stationName: fp.stationName,
      nesdis: fp.nesdis,
      temp: numProp(fp.temp),
      rh: numProp(fp.rh),
      wspd: numProp(fp.wspd),
      gust: numProp(fp.gust),
      dir: numProp(fp.dir),
      rain: numProp(fp.rain),
      recordDate: fp.recordDate,
    };
  };

  // One reusable hover popup for the whole layer.
  const hoverPopup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
    className: 'hover-popup weather-popup',
    offset: [0, -25], // Move popup up 25px to avoid covering the wind barb
  });
  state.hoverPopup = hoverPopup;

  const showHover = (e: mapboxgl.MapLayerMouseEvent) => {
    const feature = e.features?.[0];
    if (!feature || feature.geometry.type !== 'Point') return;
    map.getCanvas().style.cursor = 'pointer';
    const p = propsFromFeature(feature);
    const coords = feature.geometry.coordinates as [number, number];
    hoverPopup.setLngLat(coords).setHTML(buildHoverPopupContent(p)).addTo(map);
  };

  const onMouseEnter = (e: mapboxgl.MapLayerMouseEvent) => showHover(e);
  const onMouseMove = (e: mapboxgl.MapLayerMouseEvent) => showHover(e);
  const onMouseLeave = () => {
    map.getCanvas().style.cursor = '';
    hoverPopup.remove();
  };

  const onClick = (e: mapboxgl.MapLayerMouseEvent) => {
    const feature = e.features?.[0];
    if (!feature || feature.geometry.type !== 'Point') return;

    // Remove any existing click popups (matches prior single-open behavior).
    document.querySelectorAll('.mapboxgl-popup.weather-popup-detail').forEach(popup => popup.remove());

    const p = propsFromFeature(feature);
    const coords = feature.geometry.coordinates as [number, number];

    const popup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: true,
      className: 'click-popup weather-popup-detail',
      maxWidth: '250px',
      offset: 25, // Position popup above the marker
    })
      .setLngLat(coords)
      .setHTML(buildClickPopupContent(p))
      .addTo(map);

    // Wire Details button → WSE detail modal via window event.
    const popupEl = popup.getElement();
    const detailsBtn = popupEl?.querySelector('.wse-popup-details-btn') as HTMLButtonElement | null;
    if (detailsBtn) {
      detailsBtn.onclick = (ev) => {
        ev.stopPropagation();
        window.dispatchEvent(new CustomEvent('wse:open-detail', {
          detail: { nesdis: p.nesdis, name: p.stationName },
        }));
        popup.remove();
      };
    }

    if (!window.weatherPopups) window.weatherPopups = [];
    window.weatherPopups.push(popup);
  };

  map.on('mouseenter', WX_LAYER_ID, onMouseEnter);
  map.on('mousemove', WX_LAYER_ID, onMouseMove);
  map.on('mouseleave', WX_LAYER_ID, onMouseLeave);
  map.on('click', WX_LAYER_ID, onClick);
  state.handlers = {
    mouseenter: onMouseEnter,
    mousemove: onMouseMove,
    mouseleave: onMouseLeave,
    click: onClick,
  };

  log.debug(`Rendered ${outFeatures.length} weather symbols`);
}

export class WeatherLayerProvider extends BaseLayerProvider {
  /**
   * Live symbol-layer state, shared between the render/cleanup closures (built in
   * the constructor) and setOpacity. Assigned in the constructor after super().
   */
  private state!: WeatherLayerState;

  /**
   * Create a new WeatherLayerProvider
   */
  constructor() {
    const BACKEND_URL = getBackendUrl();

    // weather_condition shape differs by GeoServer: the legacy DP3 (MongoDB)
    // instance nests the station id/name under `header`, while the new internal
    // (PostGIS) instance returns them flat. Env-driven so each environment
    // matches whichever GeoServer its EM3 points at. Defaults nested (prod-safe).
    const nested = getWeatherConditionNested();
    const nesdisKey = nested ? 'header.nesdis' : 'nesdis';
    const nameKey = nested ? 'header.name' : 'name';
    // Layer name flips with the same flag: legacy DP3 GeoServer serves
    // `weather_condition`; the new internal GeoServer renamed EM3's
    // latest-per-station layer to `weather_condition_latest` (the bare
    // `weather_condition` there is now data_feeds' raw full-history layer).
    const wxLayer = nested ? 'weather_condition' : 'weather_condition_latest';

    // Define the data configuration
    const dataConfig: LayerDataConfig = {
      name: "Live Weather",
      shortName: "LiveWX",
      mainIcon: "fa-solid fa-cloud-bolt",
      sources: {
        "Weather Stations": {
          url: `${BACKEND_URL}/api/geoserver/geoserver/intellifire/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=intellifire%3Aweather_station&outputFormat=application%2Fjson`,
          type: 'geojson'
        },
        "Weather Conditions": {
          url: `${BACKEND_URL}/api/geoserver/geoserver/intellifire/wfs?service=WFS&version=1.0.0&request=GetFeature&typeName=intellifire:${wxLayer}&outputFormat=application/json&srsname=EPSG:4326`,
          type: 'geojson'
        }
      },
      propertyNameMapping: {
        "temp": "Temperature (°C)",
        "rh": "Relative Humidity (%)",
        "wspd": "Wind Speed (km/h)",
        "gust": "Wind Gust (km/h)",
        "rain": "Rainfall (mm)",
        [nameKey]: "Station Name"
      }
    };

    // Define the processing configuration
    const processingConfig: LayerProcessingConfig = {
      mainSource: {
        sourceName: "Weather Stations",
        idKey: "nesdis",
        mergeDirectives: [{
          targetSource: "Weather Conditions",
          targetKey: nesdisKey, // header.nesdis (legacy) or nesdis (new GeoServer)
        }]
      }
    };

    // Per-instance state for the symbol layer. The render/cleanup closures below
    // close over this object; it is also assigned to `this.state` after super()
    // so setOpacity can reach the live map/layer. Declared before renderingConfig
    // because the closures capture it (they only run after enable, so capturing
    // the not-yet-stored reference is fine — it's the same object identity).
    const state: WeatherLayerState = {
      iconIds: [],
      hoverPopup: null,
      handlers: null,
      map: null,
    };

    // Define the rendering configuration with custom functions
    const renderingConfig: LayerRenderingConfig = {
      // Return empty style since we're using markers
      styleFunction: (data: GeoJSON.FeatureCollection, helpers: LayerHelpers) => ({
        type: 'circle',
        paint: {
          'circle-radius': 0,
          'circle-opacity': 0
        }
      } as mapboxgl.CircleLayerSpecification),

      // Render the GPU symbol layer for weather stations. The `markerFunction`
      // entry point is retained (LiveLayer/LiveMapLayer drive renders through it
      // and push cleanupFunction onto teardown), but it now builds ONE geojson
      // source + symbol layer rather than per-station DOM markers.
      markerFunction: (map: mapboxgl.Map, data: GeoJSON.FeatureCollection, helpers: LayerHelpers) => {
        renderWeatherSymbolLayer(state, map, data);
      },

      // Hover/click are wired as `map.on(..., LAYER_ID, ...)` handlers inside
      // renderWeatherSymbolLayer, so these popup hooks stay no-ops.
      hoverPopupFunction: () => null,
      clickPopupFunction: () => null,

      // Tear down the symbol layer, source, registered icons, shared popups and
      // bound handlers.
      cleanupFunction: (map: mapboxgl.Map) => {
        state.map = map;
        teardownWeatherSymbolLayer(state);
      },
    };

    // Define the options
    const options: LayerOptions = {
      autoRefresh: true,
      refreshInterval: 300000, // 5 minutes
      controlPosition: 'top-left'
    };

    // Call base class constructor with our configurations
    super(dataConfig, processingConfig, renderingConfig, options);

    // Expose the closure-shared state to instance methods (setOpacity).
    this.state = state;
  }

  /**
   * #606 — dim the live-weather layer. Now a GPU symbol layer, so opacity is a
   * paint property (`icon-opacity`) rather than per-marker CSS. LiveLayer's own
   * paint loop also handles symbol layers, but the layer id here is private to
   * the provider, so apply it directly. No-op until the layer exists.
   */
  setOpacity(opacity: number): void {
    const map = this.state?.map;
    if (!map || !map.getLayer(WX_LAYER_ID)) return;
    map.setPaintProperty(WX_LAYER_ID, 'icon-opacity', clampOpacity(opacity));
  }
}